import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";

interface FacetProposal {
  family_code: string;
  prop_code: string;
  prop_name: string;
  rank: number;
  value_count: number;
  source: string;
  status: string;
  facet_type: string;
}

interface OptionMember {
  option_key: string;
  option_label: string;
  raw_value: string;
  source?: string;
}

interface AdminFacets {
  proposals: FacetProposal[];
  options: Record<string, OptionMember[]>;
}

const TYPE_OPTIONS = [
  { value: "fold", label: "Agrupar variações (formato)" },
  { value: "numeric", label: "Intervalos numéricos" },
  { value: "tokens", label: "Separar listas de características" },
  { value: "raw", label: "Valor exato" },
  { value: "ai", label: "Agrupar por significado (IA)" },
];

const STATUS_OPTIONS = [
  { value: "approved", label: "Aprovado" },
  { value: "proposed", label: "Proposto" },
  { value: "rejected", label: "Rejeitado" },
];

const STATUS_CLASS: Record<string, string> = {
  approved: "bg-green-100 text-green-700",
  proposed: "bg-amber-100 text-amber-700",
  rejected: "bg-red-100 text-red-700",
};

const typeLabel = (v: string) =>
  TYPE_OPTIONS.find((t) => t.value === v)?.label ?? v;
const statusLabel = (v: string) =>
  STATUS_OPTIONS.find((s) => s.value === v)?.label ?? v;

const base = (code: string) =>
  `${API_URLS.CATALOG_ADMIN}/categories/${encodeURIComponent(code)}`;

/** Facet manager for one category: curate props (order/type/status/rename),
 * edit canonical options, and recompute. */
const FacetManager: React.FC<{ categoryCode: string }> = ({ categoryCode }) => {
  const [facets, setFacets] = useState<AdminFacets>({
    proposals: [],
    options: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newProp, setNewProp] = useState("");
  const [newName, setNewName] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${base(categoryCode)}/facets`);
      const d = res.data;
      // Never trust the shape: a null/missing list must degrade to [] so a
      // render never throws on `.length` (that blanked the whole app before).
      setFacets({
        proposals: Array.isArray(d?.proposals) ? d.proposals : [],
        options:
          d && typeof d.options === "object" && d.options !== null
            ? d.options
            : {},
      });
      setLastUpdated(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar facets.");
    } finally {
      setLoading(false);
    }
  }, [categoryCode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    },
    [],
  );

  const patchLocal = (prop: string, patch: Partial<FacetProposal>) => {
    setFacets((f) => ({
      ...f,
      proposals: f.proposals.map((p) =>
        p.prop_code === prop ? { ...p, ...patch } : p,
      ),
    }));
  };

  const saveProp = async (p: FacetProposal) => {
    setBusy(true);
    setError(null);
    try {
      await api.put(
        `${base(categoryCode)}/facets/${encodeURIComponent(p.prop_code)}`,
        {
          prop_name: p.prop_name,
          rank: p.rank,
          facet_type: p.facet_type,
          status: p.status,
        },
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar facet.");
    } finally {
      setBusy(false);
    }
  };

  const rejectProp = async (prop: string) => {
    const p = facets.proposals.find((x) => x.prop_code === prop);
    if (!p) return;
    if (
      !confirm(`Rejeitar o facet "${prop}"? (pode restaurar mudando o estado)`)
    )
      return;
    setBusy(true);
    setError(null);
    try {
      // Reject rather than delete: a deleted prop is re-inserted by the next
      // recompute/backfill; 'rejected' is durable and restorable.
      await api.put(
        `${base(categoryCode)}/facets/${encodeURIComponent(prop)}`,
        {
          prop_name: p.prop_name,
          rank: p.rank,
          facet_type: p.facet_type,
          status: "rejected",
        },
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao rejeitar.");
    } finally {
      setBusy(false);
    }
  };

  // Reassign sequential ranks by array order; PUT only the rows that changed.
  const move = async (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= facets.proposals.length) return;
    const reordered = [...facets.proposals];
    const [item] = reordered.splice(i, 1);
    reordered.splice(j, 0, item);
    setBusy(true);
    setError(null);
    try {
      for (let k = 0; k < reordered.length; k++) {
        const p = reordered[k];
        if (p.rank !== k) {
          await api.put(
            `${base(categoryCode)}/facets/${encodeURIComponent(p.prop_code)}`,
            {
              prop_name: p.prop_name,
              rank: k,
              facet_type: p.facet_type,
              status: p.status,
            },
          );
        }
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao reordenar.");
    } finally {
      setBusy(false);
    }
  };

  const addProp = async () => {
    const code = newProp.trim();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      await api.put(
        `${base(categoryCode)}/facets/${encodeURIComponent(code)}`,
        {
          prop_name: newName.trim() || code,
          rank: facets.proposals.length,
          facet_type: "fold",
          status: "approved",
        },
      );
      setNewProp("");
      setNewName("");
      setAddOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao adicionar.");
    } finally {
      setBusy(false);
    }
  };

  const setOptions = async (prop: string, members: OptionMember[]) => {
    setBusy(true);
    setError(null);
    try {
      await api.put(
        `${base(categoryCode)}/facets/${encodeURIComponent(prop)}/options`,
        {
          members: members.filter((m) => m.option_key && m.raw_value),
        },
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar opções.");
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${base(categoryCode)}/recompute-facets`);
      setRecomputing(true);
      // The job is async and can take a while; poll a few times then stop.
      let ticks = 0;
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(async () => {
        ticks++;
        await load();
        if (ticks >= 6) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setRecomputing(false);
        }
      }, 5000);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao recomputar.");
      setRecomputing(false);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !lastUpdated)
    return <p className="text-gray-500 text-sm">A carregar facets...</p>;

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-700 px-3 py-2 rounded mb-3 text-sm flex items-center justify-between gap-2">
          <span>{error}</span>
          <button className="button is-small" onClick={load}>
            Tentar novamente
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          Facets ({facets.proposals.length})
          {lastUpdated && (
            <span className="ml-2 text-xs font-normal text-gray-400">
              atualizado {lastUpdated.toLocaleTimeString("pt-PT")}
            </span>
          )}
        </span>
        <div className="flex gap-2">
          <button
            className="button is-small flex items-center gap-1"
            onClick={() => setAddOpen((v) => !v)}
            disabled={busy}
            title="Adicionar prop"
          >
            <Plus className="w-3.5 h-3.5" /> Prop
          </button>
          <button
            className="button is-small flex items-center gap-1"
            onClick={recompute}
            disabled={busy || recomputing}
            title="Recalcular propostas + opções (usa o tipo de cada prop; os props 'ai' são agrupados pelo modelo)"
          >
            <RefreshCw
              className={"w-3.5 h-3.5 " + (recomputing ? "animate-spin" : "")}
            />
            {recomputing ? "A recalcular…" : "Recomputar"}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-2">
        O <b>tipo</b> decide como os valores ERP se transformam em opções:{" "}
        <b>Separar listas</b> divide &quot;ATX;Micro-ATX&quot; em opções
        distintas; <b>Agrupar por significado (IA)</b> junta valores que dizem o
        mesmo. Muda o tipo e <b>Recomputar</b>, ou adiciona uma opção manual
        (valor → etiqueta).
      </p>

      {addOpen && (
        <div className="flex gap-2 mb-3">
          <input
            className="input flex-1"
            placeholder="código do prop (ex.: NSAID)"
            aria-label="Código do prop"
            value={newProp}
            onChange={(e) => setNewProp(e.target.value)}
          />
          <input
            className="input flex-1"
            placeholder="nome (opcional)"
            aria-label="Nome do prop"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button
            className="button is-small is-primary"
            onClick={addProp}
            disabled={busy}
          >
            Adicionar
          </button>
        </div>
      )}

      {facets.proposals.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Sem facets. Use &quot;Recomputar&quot; para gerar candidatos.
        </p>
      ) : (
        <div className="space-y-2">
          {facets.proposals.map((p, i) => {
            const open = expanded === p.prop_code;
            const members = facets.options[p.prop_code] || [];
            const looksLikeList = members
              .slice(0, 30)
              .some((m) => /[;]/.test(m.raw_value) || /, /.test(m.raw_value));
            return (
              <div key={p.prop_code} className="border rounded">
                <div className="flex flex-wrap items-center gap-2 px-2 py-1.5">
                  <div className="flex flex-col">
                    <button
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      onClick={() => move(i, -1)}
                      disabled={busy || i === 0}
                      title="Subir"
                      aria-label="Mover para cima"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      onClick={() => move(i, 1)}
                      disabled={busy || i === facets.proposals.length - 1}
                      title="Descer"
                      aria-label="Mover para baixo"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    className="text-gray-400 hover:text-gray-700"
                    onClick={() => setExpanded(open ? null : p.prop_code)}
                    title="Opções"
                    aria-label={open ? "Colapsar opções" : "Expandir opções"}
                    aria-expanded={open}
                  >
                    {open ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  <input
                    className="input flex-1 min-w-[10rem] text-sm"
                    value={p.prop_name}
                    onChange={(e) =>
                      patchLocal(p.prop_code, { prop_name: e.target.value })
                    }
                    aria-label="Nome do facet"
                  />
                  <span className="text-xs text-gray-500 font-mono shrink-0">
                    {p.prop_code}
                  </span>
                  <span
                    className={
                      "text-xs px-2 py-0.5 rounded-full shrink-0 " +
                      (STATUS_CLASS[p.status] || "bg-gray-100 text-gray-600")
                    }
                  >
                    {statusLabel(p.status)}
                  </span>
                  <select
                    className="input text-xs"
                    value={p.facet_type}
                    onChange={(e) =>
                      patchLocal(p.prop_code, { facet_type: e.target.value })
                    }
                    aria-label="Tipo do facet"
                    title={typeLabel(p.facet_type)}
                  >
                    {TYPE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input text-xs"
                    value={p.status}
                    onChange={(e) =>
                      patchLocal(p.prop_code, { status: e.target.value })
                    }
                    aria-label="Estado do facet"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="button is-small is-primary"
                    onClick={() => saveProp(p)}
                    disabled={busy}
                  >
                    Guardar
                  </button>
                  <button
                    className="text-red-500 hover:text-red-700"
                    onClick={() => rejectProp(p.prop_code)}
                    disabled={busy}
                    title="Rejeitar"
                    aria-label="Rejeitar facet"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {looksLikeList && p.facet_type === "fold" && (
                  <div className="px-3 pb-1 text-xs text-amber-700">
                    Detetámos valores com &quot;;&quot; — o tipo{" "}
                    <b>Separar listas</b> divide-os em várias opções.
                  </div>
                )}
                {open && (
                  <OptionsEditor
                    members={members}
                    busy={busy}
                    onSave={(m) => setOptions(p.prop_code, m)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const manualOf = (m: OptionMember[]) =>
  m.filter((x) => x.source !== "computed");

/** A computed canonical option, grouped by key for display/editing. */
export interface ComputedOptionGroup {
  key: string;
  label: string;
  raws: string[];
}

/**
 * Materialises the read-only generated options as editable manual rows: one row
 * per raw ERP value, carrying its group's key+label. Used when the operator
 * starts curating manually so they edit the generated set instead of retyping
 * it. `source` is set so the rows survive `manualOf` and are sent on save (the
 * backend tags them 'manual', leaving the generated rows untouched).
 */
export const optionRowsFromGroups = (
  groups: ComputedOptionGroup[],
): OptionMember[] =>
  groups.flatMap((g) =>
    g.raws.map((r) => ({
      option_key: g.key,
      option_label: g.label,
      raw_value: r,
      source: "manual",
    })),
  );

// One canonical (computed) option: its label plus the raw ERP values it covers.
const ComputedOptionRow: React.FC<{
  group: { key: string; label: string; raws: string[] };
}> = ({ group }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-xs">
      <button
        type="button"
        className="flex w-full items-center gap-1 text-left text-gray-700 hover:text-gray-900"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="w-3 h-3 shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 shrink-0" />
        )}
        <span className="font-medium truncate">{group.label || group.key}</span>
        <span className="text-gray-500 shrink-0">({group.raws.length})</span>
      </button>
      {open && (
        <ul className="ml-4 mt-0.5 text-gray-600">
          {group.raws.map((r, i) => (
            <li key={i} className="truncate" title={r}>
              · {r}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const OptionsEditor: React.FC<{
  members: OptionMember[];
  busy: boolean;
  onSave: (m: OptionMember[]) => void;
}> = ({ members, busy, onSave }) => {
  // Only manual rows are editable/saved; the computed set is read-only (the
  // nightly backfill owns it). Saving sends the manual rows only.
  const computed = members.filter((m) => m.source === "computed");
  // Group the computed members by canonical option, so the operator can see
  // which raw ERP values each option covers.
  const computedGroups = (() => {
    const byKey = new Map<
      string,
      { key: string; label: string; raws: string[] }
    >();
    for (const m of computed) {
      const g = byKey.get(m.option_key) ?? {
        key: m.option_key,
        label: m.option_label,
        raws: [],
      };
      g.raws.push(m.raw_value);
      byKey.set(m.option_key, g);
    }
    return [...byKey.values()].sort(
      (a, b) => b.raws.length - a.raws.length || a.key.localeCompare(b.key),
    );
  })();
  const [rows, setRows] = useState<OptionMember[]>(manualOf(members));
  useEffect(() => {
    setRows(manualOf(members));
  }, [members]);

  const upd = (i: number, patch: Partial<OptionMember>) =>
    setRows((r) => r.map((m, k) => (k === i ? { ...m, ...patch } : m)));

  return (
    <div className="border-t px-2 py-2 bg-gray-50">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500">
          Opções canónicas
        </span>
        <div className="flex gap-2">
          <button
            className="button is-small"
            onClick={() =>
              setRows((r) =>
                r.length === 0 && computedGroups.length > 0
                  ? optionRowsFromGroups(computedGroups)
                  : [
                      ...r,
                      {
                        option_key: "",
                        option_label: "",
                        raw_value: "",
                        source: "manual",
                      },
                    ],
              )
            }
            title="A primeira linha converte as opções geradas em linhas editáveis"
          >
            + Linha
          </button>
          <button
            className="button is-small is-primary"
            onClick={() => onSave(rows)}
            disabled={busy}
          >
            Guardar opções
          </button>
        </div>
      </div>
      {computedGroups.length > 0 && (
        <div className="mb-2">
          <span className="text-xs text-gray-500">
            Geradas automaticamente (só leitura) — {computedGroups.length}{" "}
            opções · {computed.length} valores
          </span>
          <div className="mt-1 max-h-56 overflow-y-auto space-y-0.5 pr-1">
            {computedGroups.map((g) => (
              <ComputedOptionRow key={g.key} group={g} />
            ))}
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500">
          Sem opções manuais (usam-se as geradas). Carrega em <b>+ Linha</b>{" "}
          para converter as {computedGroups.length} opções geradas em linhas
          editáveis.
        </p>
      ) : (
        <div className="space-y-1">
          <div className="grid grid-cols-[1fr_1fr_1.5fr_24px] gap-1 text-[10px] uppercase text-gray-500">
            <span>Chave</span>
            <span>Etiqueta</span>
            <span>Valor ERP</span>
            <span />
          </div>
          {rows.map((m, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1.5fr_24px] gap-1">
              <input
                className="input text-xs"
                placeholder="chave"
                aria-label="Chave da opção"
                value={m.option_key}
                onChange={(e) => upd(i, { option_key: e.target.value })}
              />
              <input
                className="input text-xs"
                placeholder="etiqueta"
                aria-label="Etiqueta"
                value={m.option_label}
                onChange={(e) => upd(i, { option_label: e.target.value })}
              />
              <input
                className="input text-xs"
                placeholder="valor ERP exato"
                aria-label="Valor ERP"
                value={m.raw_value}
                onChange={(e) => upd(i, { raw_value: e.target.value })}
              />
              <button
                className="text-red-500 hover:text-red-700"
                onClick={() => setRows(rows.filter((_, k) => k !== i))}
                aria-label="Remover linha"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FacetManager;
